import { useEffect, useMemo, useState } from "react";
import { CalendarPlus, Check, AlertCircle, Pencil, Save, X as XIcon, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Appointments, Assessments } from "@/lib/api";

/**
 * Dialog "Agendar sessões…" — Fase 1.2 do refactor UX clínica.
 *
 * Transforma sessões do Plano Terapêutico em eventos reais da Agenda APENAS
 * quando o terapeuta seleciona linhas e confirma manualmente.
 *
 * Regras invioláveis:
 *   - Nenhuma data automática.
 *   - Nada é criado sem confirmação explícita.
 *   - Sessões já agendadas (com appointment correspondente) ficam desactivadas
 *     e marcadas com check verde — para evitar duplicação.
 *   - Linha sem data válida não cria evento (toast de aviso).
 *   - Cancelar fecha sem alterações.
 *   - Mobile-first: cards empilhados em <md, tabela compacta em ≥md.
 *
 * Usa apenas endpoints existentes: POST /api/appointments por linha confirmada.
 */

const CADENCE_PT = { semanal: "Semanal", quinzenal: "Quinzenal", mensal: "Mensal" };
const cadenceOf = (s) => {
  const t = (s?.type || "").toLowerCase();
  if (t.includes("seman")) return "semanal";
  if (t.includes("quinz")) return "quinzenal";
  return "mensal";
};

// Normaliza ISO → "YYYY-MM-DDTHH:mm" (input datetime-local) e vice-versa.
const toLocalInput = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "";
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch { return ""; }
};

export default function ScheduleSessionsDialog({
  open,
  onOpenChange,
  assessment,        // { id, patient_id, treatment_plan }
  appointments = [], // appointments já existentes para esta avaliação
  onScheduled,       // callback() chamado depois de confirmar com sucesso
}) {
  const sessions = assessment?.treatment_plan?.sessions || [];

  // Mapa rápido: session_number → appointment já marcado
  const scheduledByNumber = useMemo(() => {
    const m = {};
    appointments.forEach(ap => {
      if (ap.session_number != null && ap.scheduled_at) m[ap.session_number] = ap;
    });
    return m;
  }, [appointments]);

  // Estado por linha: { selected, datetime, alreadyScheduled, apptId, editing, busy }
  const [rows, setRows] = useState({});
  const [busy, setBusy] = useState(false);

  // Reset state cada vez que o dialog abre
  useEffect(() => {
    if (!open) return;
    const next = {};
    sessions.forEach((s, i) => {
      const existing = scheduledByNumber[s.session_number ?? i + 1];
      next[i] = {
        selected: false,
        datetime: existing ? toLocalInput(existing.scheduled_at) : "",
        alreadyScheduled: !!existing,
        apptId: existing?.id || null,
        editing: false,
        busy: false,
      };
    });
    setRows(next);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const update = (i, patch) => setRows(r => ({ ...r, [i]: { ...r[i], ...patch } }));

  // ---- Edit / Reschedule / Cancel (sessões já agendadas) ----
  const startEdit = (i) => update(i, { editing: true });
  const cancelEdit = (i) => {
    // restaurar datetime original do appointment
    const s = sessions[i];
    const existing = scheduledByNumber[s.session_number ?? i + 1];
    update(i, { editing: false, datetime: existing ? toLocalInput(existing.scheduled_at) : "" });
  };
  const saveReschedule = async (i) => {
    const r = rows[i];
    if (!r?.apptId) return;
    if (!r.datetime) {
      toast.error("Define uma data e hora válidas");
      return;
    }
    update(i, { busy: true });
    try {
      const iso = new Date(r.datetime).toISOString();
      await Appointments.update(r.apptId, { scheduled_at: iso });
      // reflete no plano (consistência ao reabrir)
      try {
        const planCopy = structuredClone(assessment.treatment_plan || {});
        const sArr = planCopy.sessions || [];
        if (sArr[i]) { sArr[i].scheduled_at = iso; }
        await Assessments.updateTreatmentPlan(assessment.id, planCopy);
      } catch { /* não bloqueante */ }
      update(i, { editing: false, busy: false });
      toast.success("Sessão remarcada");
      onScheduled?.();
    } catch {
      update(i, { busy: false });
      toast.error("Não foi possível remarcar");
    }
  };
  const cancelAppointment = async (i) => {
    const r = rows[i];
    if (!r?.apptId) return;
    const s = sessions[i];
    const ok = window.confirm(`Cancelar a marcação da sessão ${s.session_number ?? i + 1}? A sessão volta ao estado "sugerida".`);
    if (!ok) return;
    update(i, { busy: true });
    try {
      await Appointments.remove(r.apptId);
      // limpa scheduled_at no plano
      try {
        const planCopy = structuredClone(assessment.treatment_plan || {});
        const sArr = planCopy.sessions || [];
        if (sArr[i]) { delete sArr[i].scheduled_at; }
        await Assessments.updateTreatmentPlan(assessment.id, planCopy);
      } catch { /* não bloqueante */ }
      update(i, {
        alreadyScheduled: false, apptId: null, editing: false,
        selected: false, datetime: "", busy: false,
      });
      toast.success("Marcação cancelada");
      onScheduled?.();
    } catch {
      update(i, { busy: false });
      toast.error("Não foi possível cancelar");
    }
  };

  const totalSelectable = sessions.filter((_, i) => !rows[i]?.alreadyScheduled).length;
  const selectedCount = Object.entries(rows).filter(([, r]) => r.selected && !r.alreadyScheduled).length;

  const toggleAll = () => {
    const allOn = selectedCount === totalSelectable && totalSelectable > 0;
    const next = { ...rows };
    Object.keys(next).forEach(k => {
      if (!next[k].alreadyScheduled) next[k] = { ...next[k], selected: !allOn };
    });
    setRows(next);
  };

  const confirm = async () => {
    const toCreate = [];
    let invalid = 0;
    Object.entries(rows).forEach(([k, r]) => {
      if (!r.selected || r.alreadyScheduled) return;
      if (!r.datetime) { invalid += 1; return; }
      toCreate.push({ idx: Number(k), datetime: r.datetime });
    });

    if (invalid > 0) {
      toast.error(`${invalid} sessão(ões) sem data/hora`, {
        description: "Preenche data e hora ou desmarca a linha.",
      });
      return;
    }
    if (!toCreate.length) {
      toast.info("Nenhuma sessão selecionada");
      return;
    }

    setBusy(true);
    let created = 0;
    let failed = 0;
    try {
      // Cria appointments um a um (endpoint existente, sem alterações ao backend).
      // Atualiza também o scheduled_at na sessão correspondente do plano para que
      // re-abrir o dialog mostre o estado consistente.
      const planCopy = structuredClone(assessment.treatment_plan || {});
      const sArr = planCopy.sessions || [];

      for (const item of toCreate) {
        const s = sessions[item.idx];
        const iso = new Date(item.datetime).toISOString();
        try {
          await Appointments.create({
            patient_id: assessment.patient_id,
            assessment_id: assessment.id,
            scheduled_at: iso,
            duration_minutes: 60,
            session_number: s.session_number ?? item.idx + 1,
            notes: s.focus || "",
          });
          if (sArr[item.idx]) sArr[item.idx].scheduled_at = iso;
          created += 1;
        } catch {
          failed += 1;
        }
      }

      // Persiste plano com as datas confirmadas (não-bloqueante)
      try { await Assessments.updateTreatmentPlan(assessment.id, planCopy); } catch { /* */ }

      if (created > 0) {
        toast.success(`${created} sessão(ões) agendada(s)`,
          failed ? { description: `${failed} falha(s) — tenta novamente.` } : undefined);
      } else if (failed > 0) {
        toast.error("Não foi possível agendar as sessões");
      }
      onScheduled?.();
      onOpenChange(false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="schedule-sessions-dialog"
        className="max-w-2xl bg-slate-950 border-slate-800 text-slate-100 max-h-[92vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-xl text-slate-50 flex items-center gap-2">
            <CalendarPlus className="w-5 h-5 text-emerald-400" strokeWidth={1.5} />
            Agendar sessões
          </DialogTitle>
          <DialogDescription className="text-slate-400 text-sm">
            Seleciona as sessões que queres marcar e define data e hora. Sessões não selecionadas mantêm-se apenas como sugestão clínica.
          </DialogDescription>
        </DialogHeader>

        {sessions.length === 0 ? (
          <div className="rounded-xl border border-dashed border-slate-800 p-8 text-center text-slate-500 text-sm">
            Sem sessões no plano. Adiciona sessões antes de agendar.
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between text-[11px] text-slate-400 font-mono uppercase tracking-[0.18em] px-1">
              <button
                type="button"
                data-testid="toggle-all-sessions"
                onClick={toggleAll}
                disabled={totalSelectable === 0}
                className="text-sky-300 hover:text-sky-200 disabled:opacity-30 disabled:hover:text-sky-300"
              >
                {selectedCount === totalSelectable && totalSelectable > 0 ? "Desmarcar tudo" : "Selecionar todas"}
              </button>
              <span>{selectedCount}/{totalSelectable} selecionadas</span>
            </div>

            <div className="space-y-2 mt-2" data-testid="schedule-rows">
              {sessions.map((s, i) => {
                const cad = cadenceOf(s);
                const cadCls = cad === "semanal"   ? "bg-sky-500/10 border-sky-500/30 text-sky-300"
                            :  cad === "quinzenal" ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-300"
                            :                        "bg-violet-500/10 border-violet-500/30 text-violet-300";
                const r = rows[i] || { selected: false, datetime: "", alreadyScheduled: false };
                const locked = r.alreadyScheduled;
                return (
                  <div
                    key={i}
                    data-testid={`schedule-row-${i}`}
                    className={`flex flex-col md:flex-row md:items-center gap-2 md:gap-3 p-3 rounded-xl border ${
                      r.editing ? "border-amber-500/40 bg-amber-500/5" :
                      locked ? "border-emerald-500/30 bg-emerald-500/5" :
                      r.selected ? "border-sky-500/40 bg-sky-500/5" :
                      "border-slate-800 bg-slate-900/40"
                    } transition`}
                  >
                    <label className="flex items-center gap-3 md:flex-1 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        data-testid={`schedule-check-${i}`}
                        checked={locked || r.selected}
                        disabled={locked}
                        onChange={(e) => update(i, { selected: e.target.checked })}
                        className="w-4 h-4 accent-sky-500 disabled:opacity-60"
                      />
                      <div className="w-7 h-7 rounded-full bg-sky-500/10 border border-sky-500/30 text-sky-300 text-[11px] flex items-center justify-center font-mono shrink-0">
                        {s.session_number ?? i + 1}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`text-[10px] px-2 py-0.5 rounded-full border font-mono uppercase tracking-[0.12em] ${cadCls}`}>
                            {CADENCE_PT[cad]}
                          </span>
                          {locked && !r.editing && (
                            <span className="text-[10px] px-2 py-0.5 rounded-full border border-emerald-500/40 bg-emerald-500/10 text-emerald-300 font-mono uppercase tracking-[0.12em] inline-flex items-center gap-1">
                              <Check className="w-3 h-3" /> Agendada
                            </span>
                          )}
                          {r.editing && (
                            <span className="text-[10px] px-2 py-0.5 rounded-full border border-amber-500/40 bg-amber-500/10 text-amber-300 font-mono uppercase tracking-[0.12em]">
                              A remarcar
                            </span>
                          )}
                        </div>
                        <div className="text-sm text-slate-100 truncate mt-0.5">{s.focus || "Sessão"}</div>
                      </div>
                    </label>
                    <input
                      type="datetime-local"
                      data-testid={`schedule-datetime-${i}`}
                      disabled={locked && !r.editing}
                      value={r.datetime}
                      onChange={(e) => update(i, { datetime: e.target.value, selected: !locked && !!e.target.value })}
                      className="bg-slate-900 border border-slate-700 rounded-md px-2 py-1.5 text-sm text-slate-100 focus:outline-none focus:border-sky-500 disabled:opacity-60 md:w-56"
                    />
                    {locked && (
                      <div className="flex items-center gap-1 self-end md:self-auto">
                        {!r.editing ? (
                          <>
                            <button
                              type="button"
                              data-testid={`schedule-edit-${i}`}
                              onClick={() => startEdit(i)}
                              disabled={r.busy}
                              title="Remarcar"
                              className="p-1.5 rounded-md text-slate-400 hover:text-sky-300 hover:bg-sky-500/10 disabled:opacity-40"
                            >
                              <Pencil className="w-3.5 h-3.5" />
                            </button>
                            <button
                              type="button"
                              data-testid={`schedule-cancel-appt-${i}`}
                              onClick={() => cancelAppointment(i)}
                              disabled={r.busy}
                              title="Cancelar marcação"
                              className="p-1.5 rounded-md text-slate-400 hover:text-rose-400 hover:bg-rose-500/10 disabled:opacity-40"
                            >
                              <RotateCcw className="w-3.5 h-3.5" />
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              type="button"
                              data-testid={`schedule-save-${i}`}
                              onClick={() => saveReschedule(i)}
                              disabled={r.busy}
                              title="Guardar nova data/hora"
                              className="p-1.5 rounded-md text-emerald-300 hover:text-emerald-200 hover:bg-emerald-500/10 disabled:opacity-40"
                            >
                              <Save className="w-3.5 h-3.5" />
                            </button>
                            <button
                              type="button"
                              data-testid={`schedule-edit-cancel-${i}`}
                              onClick={() => cancelEdit(i)}
                              disabled={r.busy}
                              title="Descartar alteração"
                              className="p-1.5 rounded-md text-slate-400 hover:text-slate-200 hover:bg-slate-800 disabled:opacity-40"
                            >
                              <XIcon className="w-3.5 h-3.5" />
                            </button>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="flex items-start gap-2 mt-2 px-1 text-[11px] text-slate-500">
              <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <span>Linhas selecionadas sem data/hora não serão criadas. Sessões já agendadas podem ser remarcadas (<Pencil className="w-3 h-3 inline" />) ou canceladas (<RotateCcw className="w-3 h-3 inline" />) sem duplicar.</span>
            </div>
          </>
        )}

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            data-testid="schedule-cancel-btn"
            onClick={() => onOpenChange(false)}
            disabled={busy}
            className="rounded-full border-slate-700 text-slate-300 hover:bg-slate-800"
          >
            Fechar
          </Button>
          <Button
            data-testid="schedule-confirm-btn"
            onClick={confirm}
            disabled={busy || sessions.length === 0 || selectedCount === 0}
            className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 rounded-full px-5 glow-emerald disabled:opacity-50"
          >
            <CalendarPlus className="w-4 h-4 mr-2" />
            {busy ? "A agendar…" : `Confirmar (${selectedCount})`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
